import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, from, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  ACCOUNTS,
  GET_ALL_REFUNDS_WITH_PAGINATION,
  GET_ALL_VENDOR_INVOICES,
  GET_REFUND_BY_ID,
  LIST_ACCOUNTS,
  LIST_PAID_INVOICES,
  LIST_VENDORS,
} from '../graphql/refund.queries';
import {
  INITIATE_REFUND,
  CANCEL_REFUND_BY_ID,
  APPROVE_OR_REJECT_REFUND,
  MODIFY_REFUND,
  GENERATE_REFUND_PDF,
} from '../graphql/refund.mutation';

@Injectable({ providedIn: 'root' })
export class RefundService {
  constructor(private http: HttpClient, private apollo: Apollo) {}

  getAllRefunds(input: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_ALL_REFUNDS_WITH_PAGINATION,
        variables: {
          input: input,
        },
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(
        map((response) => response.data?.findAllRefundsWithPagination)
      );
  }

  removeRefund(refundId: number): Observable<any> {
    return this.apollo.use('accounts').mutate({
      mutation: CANCEL_REFUND_BY_ID,
      variables: {
        refundId,
      },
    });
  }

  getAllVendorInvoices(input: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_ALL_VENDOR_INVOICES,
        variables: {
          input: input,
        },
        fetchPolicy: 'no-cache',
      })
      .valueChanges.pipe(
        map((response) => response.data?.findAllBillsWithPagination)
      );
  }

  fetchVendors(search: any): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: LIST_VENDORS,
        variables: {
          search,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.vendors || []));
  }

  listPaidInvoices(customerId: any, search: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: LIST_PAID_INVOICES,
        variables: { customerId, search },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.getPaidInvoices || []));
  }

  fetchAccounts(search: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: ACCOUNTS,
        variables: { search },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.accounts || []));
  }

  initiateRefund(createRefundInput: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: INITIATE_REFUND,
        variables: {
          createRefundInput,
        },
      })
      .pipe(map((result: any) => result.data.initiateRefund || []));
  }

  fetchRefundById(id: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_REFUND_BY_ID,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.refund || []));
  }

  approveOrRejectRefund(refundId: any, isApproved: any): Observable<any> {
    return this.apollo.use('accounts').mutate({
      mutation: APPROVE_OR_REJECT_REFUND,
      variables: {
        refundId: refundId,
        isApproved: isApproved,
      },
    });
  }

  modifyRefund(updateRefundInput: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: MODIFY_REFUND,
        variables: {
          updateRefundInput,
        },
      })
      .pipe(map((result: any) => result.data.modifyRefund || []));
  }

  generateRefundPdf(refundId: any): Observable<any> {
    return this.apollo.use('accounts').mutate({
      mutation: GENERATE_REFUND_PDF,
      variables: {
        refundId: refundId,
      },
    });
  }
}
